import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function TooltipRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults: surface contract on TooltipTrigger's rendered
  // div (the trigger is always in the DOM; the content portals on hover).
  const atomDefaults = useAtomDefaults("tooltip")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const tooltipText = element.text ? String(resolveSymbol(element.text) ?? "") : ""

  const trigger = element.children?.[0]

  // defaultOpen: force-open uncontrolled state (preview / always-visible use
  // cases). Matches the pattern on alert-dialog / popover / collapsible
  // renderers. `variant` is not overloaded here, but staying consistent.
  const defaultOpen = (element as unknown as Record<string, unknown>).defaultOpen === true

  return (
    <TooltipProvider>
      <Tooltip defaultOpen={defaultOpen}>
        <TooltipTrigger
          className={cn("inline-block", element.className)}
          data-testid={`tooltip-${element.id}`}
          data-atom-type="tooltip"
          data-variant={resolvedVariant}
          data-size={resolvedSize}
          render={<div />}
        >
          {trigger && <ElementRenderer element={trigger} />}
        </TooltipTrigger>
        {tooltipText && (
          <TooltipContent sideOffset={4}>
            {tooltipText}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  )
}
